import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { supabase } from '../supabaseClient';
import './Eventdetailpage.css';
import { Link } from 'react-router-dom';

const EventDetailPage = () => {
    const navigate = useNavigate();

    const heroRef = useRef(null);
    const heroContentRef = useRef(null);
    const metaCardRef = useRef(null);
    const locationCardRef = useRef(null);
    const ticketsCardRef = useRef(null);
    const aboutRef = useRef(null);
    const pointsRef = useRef(null);
    const footerRef = useRef(null);

    const [hero, setHero] = useState(null);
    const [about, setAbout] = useState(null);
    const [points, setPoints] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const getData = async () => {
            const heroRes = await supabase.from('details_hero').select('*');
            const aboutRes = await supabase.from('details_about').select('*');
            const pointsRes = await supabase.from('details_about_points').select('*').order('display_order');

            setHero(heroRes.data?.[0] || null);
            setAbout(aboutRes.data?.[0] || null);
            setPoints(pointsRes.data || []);
            setLoading(false);
        };
        getData();
    }, []);

    useEffect(() => {
        if (loading) return;

        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

        gsap.set(heroRef.current, { opacity: 0, scale: 1.06 });
        gsap.set(heroContentRef.current, { opacity: 0, y: 30 });
        gsap.set(metaCardRef.current, { opacity: 0, y: 24 });
        gsap.set(locationCardRef.current, { opacity: 0, y: 20 });
        gsap.set(ticketsCardRef.current, { opacity: 0, y: 20 });
        gsap.set(aboutRef.current, { opacity: 0, x: -24 });
        if (pointsRef.current?.children?.length) {
            gsap.set(Array.from(pointsRef.current.children), { opacity: 0, x: -16 });
        }
        gsap.set(footerRef.current, { opacity: 0, y: 30 });

        tl.to(heroRef.current, { opacity: 1, scale: 1, duration: 0.9 })
          .to(heroContentRef.current, { opacity: 1, y: 0, duration: 0.7 }, '-=0.5')
          .to(metaCardRef.current, { opacity: 1, y: 0, duration: 0.6 }, '-=0.3')
          .to(locationCardRef.current, { opacity: 1, y: 0, duration: 0.55 }, '-=0.3')
          .to(ticketsCardRef.current, { opacity: 1, y: 0, duration: 0.55 }, '-=0.3')
          .to(aboutRef.current, { opacity: 1, x: 0, duration: 0.6 }, '-=0.2')
          .to(
              pointsRef.current?.children?.length ? Array.from(pointsRef.current.children) : aboutRef.current,
              { opacity: 1, x: 0, stagger: 0.1, duration: 0.45 },
              '-=0.3'
          )
          .to(footerRef.current, { opacity: 1, y: 0, duration: 0.5, ease: 'back.out(1.3)' }, '-=0.2');
    }, [loading]);

    if (loading) {
        return (
            <div className="home-wrapper">
                <div className="mobile-container">
                    <div className="ed-screen ed-screen--loading" role="status" aria-label="Loading event details">
                        <div className="ed-skeleton-hero"></div>
                        <div className="ed-skeleton-body">
                            <div className="ed-skeleton-bar"></div>
                            <div className="ed-skeleton-bar ed-skeleton-bar--short"></div>
                            <div className="ed-skeleton-bar"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="home-wrapper">
            <div className="mobile-container">
                <div className="ed-screen">

                    <header className="ed-hero" ref={heroRef}>
                        {hero?.hero_image
                            ? <img src={hero.hero_image} alt={`Featured image for ${hero.title_en}`} className="ed-hero-img" />
                            : <div className="ed-hero-placeholder" aria-hidden="true"></div>
                        }
                        <div className="ed-hero-overlay" aria-hidden="true"></div>

                        <div className="ed-topbar">
                            <button className="ed-icon-btn" onClick={() => navigate(-1)} aria-label="Go back">
                                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M12 5l-7 7 7 7"/>
                                </svg>
                            </button>
                            <div className="ed-topbar-right">
                                <button className="ed-icon-btn" aria-label="Share this event">
                                    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                                    </svg>
                                </button>
                                <button className="ed-icon-btn" aria-label="Save this event">
                                    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="ed-hero-content" ref={heroContentRef}>
                            <span className="ed-label">{hero?.label_en || 'EXHIBITION'}</span>
                            <h1 className="ed-hero-title">{hero?.title_en || 'Architecture & Space'}</h1>
                        </div>
                    </header>

                    <main className="ed-body">

                        <section className="ed-meta-card" ref={metaCardRef} aria-label="Event Date and Time">
                            <div className="ed-meta-item">
                                <span className="ed-meta-icon ed-meta-icon--red" aria-hidden="true">
                                    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="4" width="18" height="18" rx="2"/>
                                        <line x1="16" y1="2" x2="16" y2="6"/>
                                        <line x1="8" y1="2" x2="8" y2="6"/>
                                        <line x1="3" y1="10" x2="21" y2="10"/>
                                    </svg>
                                </span>
                                <div>
                                    <p className="ed-meta-label">DATE</p>
                                    <p className="ed-meta-value">May 20, 2026</p>
                                </div>
                            </div>
                            <div className="ed-meta-divider" aria-hidden="true"></div>
                            <div className="ed-meta-item">
                                <span className="ed-meta-icon ed-meta-icon--red" aria-hidden="true">
                                    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10"/>
                                        <polyline points="12 6 12 12 16 14"/>
                                    </svg>
                                </span>
                                <div>
                                    <p className="ed-meta-label">TIME</p>
                                    <p className="ed-meta-value">2:00 PM</p>
                                </div>
                            </div>
                        </section>

                        <section className="ed-info-card" ref={locationCardRef} aria-labelledby="location-title">
                            <div className="ed-info-card-header">
                                <span className="ed-info-icon" aria-hidden="true">
                                    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                        <circle cx="12" cy="10" r="3"/>
                                    </svg>
                                </span>
                                <p className="ed-info-label" id="location-title">LOCATION</p>
                            </div>
                            <p className="ed-info-primary">Cairo Exhibition Center</p>
                            <p className="ed-info-secondary">Nasr City, Cairo, Egypt</p>
                        </section>

                        <section className="ed-info-card" ref={ticketsCardRef} aria-labelledby="tickets-title">
                            <div className="ed-info-card-header">
                                <span className="ed-info-icon" aria-hidden="true">
                                    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                        <circle cx="9" cy="7" r="4"/>
                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                    </svg>
                                </span>
                                <p className="ed-info-label" id="tickets-title">AVAILABLE TICKETS</p>
                            </div>
                            <p className="ed-info-primary">180 tickets remaining</p>
                            <div className="ed-bar-track" role="progressbar" aria-valuenow="180" aria-valuemin="0" aria-valuemax="200" aria-label="Tickets remaining">
                                <div className="ed-bar-fill" style={{ width: '90%' }}></div>
                            </div>
                        </section>

                        <section className="ed-about" ref={aboutRef} aria-labelledby="about-title">
                            <h2 className="ed-about-title" id="about-title">
                                {about?.title_en || 'ABOUT THIS EVENT'}
                            </h2>
                            <p className="ed-about-text">{about?.content_en}</p>

                            {about?.philosophy_title_en && (
                                <>
                                    <h3 className="ed-philosophy-title">{about.philosophy_title_en}</h3>
                                    <p className="ed-about-text">{about.philosophy_content_en}</p>
                                </>
                            )}
                        </section>

                        {points.length > 0 && (
                            <ul className="ed-points" ref={pointsRef} aria-label="Key highlights">
                                {points.map((p) => (
                                    <li className="ed-point-item" key={p.id}>
                                        <span className="ed-point-dot" aria-hidden="true">
                                            <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12"/>
                                            </svg>
                                        </span>
                                        <p className="ed-point-text">{p.point_en}</p>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <div className="ed-subtitle-text">
                            {hero?.subtitle_en && <p className="ed-about-text">{hero.subtitle_en}</p>}
                        </div>

                    </main>

                    <footer className="ed-footer" ref={footerRef}>
                        <div className="ed-price-wrap">
                            <span className="ed-price-label">TICKET PRICE</span>
                            <span className="ed-price"><span className="sr-only">Currency: </span>EGP <span className="ed-price-num">250</span></span>
                        </div>
                        <div className="ed-footer-btns">
                            <a
                                href="https://discover.matterport.com/space/DeRaVNjhz7j"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ed-view-btn"
                                aria-label="View 3D Tour of the exhibition (opens in new tab)"
                            >
                                <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                    <polyline points="15 3 21 3 21 9"/>
                                    <line x1="10" y1="14" x2="21" y2="3"/>
                                </svg> VIEW
                            </a>

                            <Link to="/booking"
                                className="ed-book-btn"
                                aria-label="Proceed to book your ticket"
                            >
                                BOOK NOW
                            </Link>
                        </div>
                    </footer>

                </div>
            </div>
        </div>
    );
};

export default EventDetailPage;